import { useNavigate } from 'react-router-dom';
import { Badge, Icon, MobileHeader, QueryState, ListSkeleton } from '@/components';
import { useCertificates } from '@/hooks/queries';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import type { CertStatus } from '@/types/domain';

const ST: Record<CertStatus, { k: 'sage' | 'ochre' | 'clay' | 'gray'; l: string }> = {
  valid: { k: 'sage', l: 'Valid' },
  expiring: { k: 'ochre', l: 'Expiring' },
  expired: { k: 'clay', l: 'Expired' },
  revoked: { k: 'gray', l: 'Revoked' },
};

export function LearnerWallet() {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const certsQuery = useCertificates();
  const certs = certsQuery.data ?? [];

  const counts = {
    valid: certs.filter((c) => c.status === 'valid').length,
    expiring: certs.filter((c) => c.status === 'expiring').length,
    expired: certs.filter((c) => c.status === 'expired').length,
  };

  const cards = (
    <QueryState query={certsQuery} skeleton={<ListSkeleton rows={3} />} isEmpty={() => certs.length === 0} emptyIcon="award" emptyTitle="No certificates yet" emptyHint="Finish a course to earn your first certificate.">
      {() => (
        <div style={isDesktop ? { display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 18 } : { display: 'flex', flexDirection: 'column', gap: 14 }}>
      {certs.map((c) => {
        const st = ST[c.status];
        return (
          <button key={c.id} onClick={() => navigate(`/learn/wallet/${c.id}`)} style={{ all: 'unset', cursor: 'pointer', display: 'block' }}>
            <div className="card lift" style={{ overflow: 'hidden' }}>
              <div style={{ display: 'flex', gap: 14, padding: 16, alignItems: 'center' }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, flex: 'none', background: st.k === 'gray' ? 'var(--ink-4)' : `var(--${st.k})`, opacity: 0.92, display: 'grid', placeItems: 'center', color: 'var(--on-accent)' }}>
                  <Icon name={c.status === 'revoked' ? 'x' : 'award'} size={22} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.3 }}>{c.course}</div>
                  <div className="th" style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 1 }}>{c.th}</div>
                </div>
                <Badge kind={st.k} dot>{st.l}</Badge>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '11px 16px', borderTop: '1px solid var(--line)', background: 'var(--card-2)' }}>
                <span className="u-mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>{c.id}</span>
                <span style={{ fontSize: 12.5, color: c.status === 'expired' ? 'var(--clay)' : 'var(--ink-3)' }}>Expires {c.expires}</span>
              </div>
            </div>
          </button>
        );
      })}
        </div>
      )}
    </QueryState>
  );

  if (isDesktop) {
    return (
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 600, letterSpacing: '-.02em', margin: 0 }}>Certificates</h1>
            <div className="th muted" style={{ fontSize: 14, marginTop: 4 }}>ใบรับรองของฉัน</div>
          </div>
          <button className="btn btn-ghost"><Icon name="shield" size={16} /> Verify a certificate</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 22 }}>
          <StatTile n={counts.valid} label="Valid" />
          <StatTile n={counts.expiring} label="Expiring" color="var(--ochre)" />
          <StatTile n={counts.expired} label="Expired" color="var(--clay)" />
        </div>
        {cards}
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--paper)', minHeight: '100%' }}>
      <MobileHeader
        title="Wallet"
        th="ใบรับรองของฉัน"
        right={<button style={{ padding: 9, borderRadius: 99, border: 'none', background: 'transparent', cursor: 'pointer' }}><Icon name="shield" size={20} /></button>}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '14px 20px 28px' }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <StatTile n={counts.valid} label="Valid" />
          <StatTile n={counts.expiring} label="Expiring" color="var(--ochre)" />
          <StatTile n={counts.expired} label="Expired" color="var(--clay)" />
        </div>
        {cards}
      </div>
    </div>
  );
}

function StatTile({ n, label, color }: { n: number; label: string; color?: string }) {
  return (
    <div className="card" style={{ flex: 1, padding: 14, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontSize: 24, fontWeight: 600, color: color ?? 'var(--ink)' }}>{n}</div>
      <div className="eyebrow" style={{ marginTop: 4 }}>{label}</div>
    </div>
  );
}
